import Account from "./account.js";
import {autoCitationsModes} from "./citation.js";
import moment from "moment";
import {account, accountUuid, project} from "./data/model/index.js";

const SHARE_INVITED = "invited";
const SHARE_PENDING = "pending";
const SHARE_ACCEPTED = "accepted";
const SHARE_DECLINED = "declined";

const ACCESS_READONLY = "readonly";
const ACCESS_COMMENT = "comment";
const ACCESS_EDIT = "edit";
const ACCESS_ADMIN = "admin";

// not exported
const DEFAULT_CITATION_STYLE = "modern-language-association";

const getOwnerUuid = function (project: project): string | undefined {
	if (project && typeof project === "object" && project.account_uuid)
		return project.account_uuid;
	else
		return undefined;
};

const isOwner = function (account: account, project: project): boolean {
	const accountUuid = Account.getUuidFromAccount(account);
	const projectOwnerUuid = getOwnerUuid(project);

	if (accountUuid === undefined || projectOwnerUuid === undefined)
		return false; // error condition
	else
		return accountUuid === projectOwnerUuid;
};

/**
 * Returns true if the account has the requested level of access (or higher) on the specified project.
 *
 * We do this by checking the "accepted" share list in the project, except when
 * the requested access is "readonly", where we also include the "pending" list.
 * The result is always true for the project owner, unless they are pending the time-tracking terms review.
 * The result is always false when time-tracking is enabled and the account requests something more than read access but has not yet reviewed the time-tracking terms.
 *
 * See Service/Project::hasAccess() in pn-api.
 *
 * @param  {Object}  account
 * @param  {Object}  project
 * @param  {string}  access  See ACCESS_* consts
 * @return {boolean}
 */
// TODO: access string consts type?
const hasAccess = function (account: account, project: project, access: string): boolean {
	if (!project || !account)
		return false;

	if (project.status === "archived")
		return false;

	const accountUuid = Account.getUuidFromAccount(account);

	if (accessInt(access) > accessInt(ACCESS_READONLY) && timeTrackingTermsPendingReview(account, project))
		return false;

	if (access === ACCESS_EDIT && isProjectLocked(project))
		return false;

	if (getOwnerUuid(project) === accountUuid)
		return true;

	let userAccess = inSharingList(project, accountUuid, SHARE_ACCEPTED);
	if (!userAccess && access === ACCESS_READONLY && inSharingList(project, accountUuid, SHARE_PENDING))
		userAccess = ACCESS_READONLY;
	if (!userAccess)
		return false;

	const acceptableAccessLevels = accessLevelsThatInclude(access);
	return acceptableAccessLevels.includes(userAccess);
};

/**
 * Returns the given access as a number, useful for comparison.
 *
 * @param {string} access
 * @return {number}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'access' implicitly has an 'any' type.
const accessInt = function (access) {
	const sortedAccessLevels = allAccessLevels();
	if (!sortedAccessLevels.includes(access))
		throw new Error("Invalid access level: " + access);
	return sortedAccessLevels.indexOf(access);
};

/**
 * Returns the given access level and all other levels above it.
 * See Service/Project::accessLevelsThatInclude() in pn-api.
 *
 * @param {string} access
 * @return {string[]}
 * @throws Exception if invalid access level is specified
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'access' implicitly has an 'any' type.
const accessLevelsThatInclude = function (access) {
	const sortedAccessLevels = allAccessLevels();
	if (!sortedAccessLevels.includes(access))
		throw new Error("Invalid access level: " + access);

	const ret = [];
	let flag = false;
	for (const level of sortedAccessLevels) {
		if (level === access)
			flag = true;

		if (flag)
			ret.push(level);
	}

	return ret;
};

/**
 * Returns an array of access levels, sorted from least amount of access to most.
 * See Service/Project::allAccessLevels() in pn-api.
 */
const allAccessLevels = function (): string[] {
	return [
		ACCESS_READONLY,
		ACCESS_COMMENT,
		ACCESS_EDIT,
		ACCESS_ADMIN
	];
};

/**
 * See Service/Project::inSharingList() in pn-api.
 *
 * @param {Object} project
 * @param {string} uuidOrEmail  A user's uuid or email
 * @param {string} list         One of the SHARE_* constants
 * @return {string|boolean}     The access level (ACCESS_*), or false if not found
 * @throws Exception if list is not a valid SHARE_* constant
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const inSharingList = function (project, uuidOrEmail, list) {
	const validShareTypes = [SHARE_INVITED, SHARE_PENDING, SHARE_ACCEPTED, SHARE_DECLINED];
	if (!validShareTypes.includes(list))
		throw new Error("Invalid share list type: " + list);

	if (!project || typeof project !== "object" || typeof project.sharing !== "object")
		return false;
	if (!project.sharing[list] || !Array.isArray(project.sharing[list]))
		return false;

	const key = (list === SHARE_INVITED ? "email" : "uuid");

	//
	// TODO: Is this true?
	// In pn-api some magic happens here to check for valid emails/uuids.
	// It's probably not necessary here, since pn-api determines the data in these arrays.
	//

	return _inSharingList(uuidOrEmail, project.sharing[list], key);
};

/**
 * Not an exported function.
 * See Service/Project::allAccessLevels() in pn-api.
 *
 * @param  {string}   item Either an email address or an account uuid
 * @param  {Object[]} list One of the "sharing" lists from a project object
 * @param  {string}   key  "uuid" or "email"
 * @return {string|boolean} The access level (ACCESS_*), or false if not found
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'item' implicitly has an 'any' type.
const _inSharingList = function (item, list, key = "uuid") {
	for (const record of list) {
		if (record[key] === item)
			return record.access;
	}

	return false;
};

/**
 * Look up the given account uuid in the project shares.
 * Return a human-readable name for the user, or a placeholder if not found.
 * This isn't needed when working with shares directly, but is helpful for
 * displaying the name of a user that created an annotation.
 *
 * We don't cache results here because a user's name could theoretically change.
 * It is recommended to use memoization in the frontend where this is used.
 * Be wary of caching the placeholder value.
 *
 * @param {string} resolveAccountUuid  Account uuid to resolve
 * @param {string} currentAccountUuid  Account uuid of the current user
 * @param {Object} project
 * @return {string}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an '... Remove this comment to see the full error message
const getUserNameFromShare = function (resolveAccountUuid: accountUuid, currentAccountUuid: accountUuid, project): string {
	const deleted = "Deleted User";
	const unknown = "Unknown User";

	// @ts-expect-error TS(7006) FIXME: Parameter 'o' implicitly has an 'any' type.
	const isObj = (o) => typeof o === "object";

	// Try to return 'unknown' if the data provided is obviously invalid
	if (!resolveAccountUuid || !project || !isObj(project))
		return unknown;

	// This has to be after the above checks. Otherwise null, undefined, etc., are problematic.
	if (resolveAccountUuid === currentAccountUuid)
		return "Me";

	if (isObj(project.computed) && isObj(project.computed.account)) {
		if (resolveAccountUuid === project.computed.account.uuid)
			return (project.computed.account.name ? project.computed.account.name : unknown);
	}

	// Since resolveAccountUuid isn't the current user, or the owner,
	// make sure we actually have some sharing data to check. If we don't,
	// something is wrong. If we do and we later don't find the user,
	// they probably were deleted.
	if ((!isObj(project.computed) || !isObj(project.computed.account)) && !isObj(project.participants) && !isObj(project.sharing))
		return unknown;

	// We usually should find users in the participants list (assuming they have contributed)...
	if (isObj(project.participants)) {
		if (resolveAccountUuid in project.participants) {
			return (project.participants[resolveAccountUuid].name
				? project.participants[resolveAccountUuid].name
				: unknown);
		}
	}

	// ... But also check the shares list in case this gets used somewhere unexpected.
	if (isObj(project.sharing)) {
		// We ignore the SHARE_INVITED list because they probably haven't created annotations yet.
		for (const list of [SHARE_ACCEPTED, SHARE_PENDING, SHARE_DECLINED]) {
			if (!project.sharing[list] || !Array.isArray(project.sharing[list]))
				continue;

			for (const share of project.sharing[list]) {
				if (share.uuid === resolveAccountUuid)
					return (share.name ? share.name : unknown);
			}
		}
	}

	// Everything else failed, so probably the user was deleted after contributing to the project.
	return deleted;
};

/**
 * Returns true if the user accepted the terms, false if they rejected them, or null if they haven't reviewed them.
 *
 * Does not take `time_tracking_enabled` into consideration. Callers should normally check that the property is `true`
 * before calling this function.
 *
 * @param  {Object}  account
 * @param  {Object}  project
 * @return {boolean|null}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
const timeTrackingTermsAccepted = function (account, project) {
	if (!project || typeof project !== "object" || !project.time_tracking_terms)
		return null;

	const accountUuid = Account.getUuidFromAccount(account);
	if (!accountUuid)
		return null;

	// @ts-expect-error TS(7006) FIXME: Parameter 'i' implicitly has an 'any' type.
	const item = project.time_tracking_terms.find(i => i.uuid === accountUuid);
	return item ? item.accepted : null;
};

/**
 * Returns true if the user needs to review the terms (i.e. the project has
 * time-tracking enabled and the user hasn't reviewed them yet).
 *
 * @param  {Object}  account
 * @param  {Object}  project
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
const timeTrackingTermsPendingReview = function (account, project) {
	if (!project || typeof project !== "object")
		return false;

	if (!project.time_tracking_enabled)
		return false;

	if (_timeTrackingTermsRequired(account, project))
		return !!(timeTrackingTermsAccepted(account, project) === null);
	else
		return false;
};

/**
 * Returns true of the user is required to agree to Assessment terms to use the project outline.
 * Some types of share recipients can use the project without having Assessment track them.
 *
 * @param  {Object}  account
 * @param  {Object}  project
 * @return {boolean}
 * @throws Error if project data is missing or user doesn't appear to have access to the project.
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'account' implicitly has an 'any' type.
const _timeTrackingTermsRequired = function (account, project) {
	// User must agree to terms if they have edit/admin access to project,
	// but not for only readonly/comment access.

	if (!project)
		throw new Error("_timeTrackingTermsRequired(): No project given.");
	if (!project.time_tracking_enabled)
		return false;

	const accountUuid = Account.getUuidFromAccount(account);
	if (getOwnerUuid(project) === accountUuid)
		return true;

	// Account for both accepted and pending shares,
	// since this function could be used in deciding
	// whether to show the accept-terms modal.
	let userAccess = inSharingList(project, accountUuid, SHARE_ACCEPTED);
	if (!userAccess)
		userAccess = inSharingList(project, accountUuid, SHARE_PENDING);
	if (!userAccess)
		throw new Error("_timeTrackingTermsRequired(): User has no access to project.");

	const levelsRequiringAgreement = accessLevelsThatInclude(ACCESS_EDIT);
	return levelsRequiringAgreement.includes(userAccess);
};

/**
 * Returns the preferred citation style for the project,
 * or the product default style if none has been selected.
 *
 * @param {Object} project
 * @return {string}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const getCitationStyle = function (project) {
	return (project?.settings?.citations_preferred_format)
		? project.settings.citations_preferred_format
		: DEFAULT_CITATION_STYLE;
};

/**
 * Get the auto citations type. If there is none in the project data,
 * defaults to automatic.
 *
 * @param {Object} project
 * @return {string}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const getProjectAutoCitationsMode = function (project) {
	if (project.computed === undefined || project.computed.auto_citations === undefined)
		return autoCitationsModes.AUTOMATIC;

	return project.computed.auto_citations;
};

/**
 * Check sharing access at the project level (influenced by assignment settings).
 *
 * @param {Object} project
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const hasSharingSettingsAccess = function (project) {
	if (!project || typeof project !== "object")
		return true;

	return !(project.computed?.sharing_lock);
};

/**
 * Check topic editing access at the project level (influenced by assignment settings).
 *
 * @param {Object} project
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const hasTopicsEditingAccess = function (project) {
	if (!project || typeof project !== "object")
		return true;

	if (isProjectLocked(project))
		return false;

	return !(project.computed?.topics_lock);
};

/**
 * Check source note label editing access at the project level (influenced by
 * assignment settings).
 *
 * @param {Object} project
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const hasSourceNoteLabelsEditingAccess = function (project) {
	if (!project || typeof project !== "object")
		return true;

	if (isProjectLocked(project))
		return false;

	return !(project.computed?.source_notes_lock);
};

/**
 * Check topic adding availability at the project level (influenced by assignment settings).
 *
 * @param {Object} project
 * @param {number} currentNumTopics current number of topics in the project
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const canAddNewTopics = function (project, currentNumTopics) {
	if (!project || typeof project !== "object")
		return true;

	if (project.computed) {
		const {topics_lock, topics_max} = project.computed;

		if (isProjectLocked(project))
			return false;
		if (topics_lock)
			return false;
		else if (topics_max)
			return topics_max > currentNumTopics;
	}

	return true;
};

/**
 * Get the due date if it exists. Returns null if it does not.
 *
 * @param {Object} project
 * @return {string|null}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const getDueDate = function (project) {
	if (!project || typeof project !== "object")
		return null;

	if (project.computed?.assignment_due_dt)
		return project.computed.assignment_due_dt;

	return null;
};

/**
 * Checks if the project is locked due to assignment settings.
 *
 * @param {Object} project
 * @return {boolean}
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'project' implicitly has an 'any' type.
const isProjectLocked = function (project) {
	if (!project || typeof project !== "object")
		return false;

	if (project.computed?.assignment_project_lock) {
		switch (project.computed.assignment_project_lock) {
			case "available":
				return false;

			case "locked":
				return true;

			case "lock-on-due-date":
				return moment(project.computed.assignment_due_dt).isBefore(moment());
		}
	}

	return false;
};

const getProjectAiCreditUsage = (project: project): number | null => {
	const {ai_credits_used = 0, computed} = project;
	const ai_credit_limit = computed?.ai_credit_limit ?? 0;

	// There is no limit
	if (ai_credit_limit === 0)
		return null;

	// This can be over 100%, if the limit was lowered after credits were used
	const usage = ai_credits_used / ai_credit_limit;

	return usage;
};

export default {
	// consts
	SHARE_INVITED,
	SHARE_PENDING,
	SHARE_ACCEPTED,
	SHARE_DECLINED,
	ACCESS_READONLY,
	ACCESS_COMMENT,
	ACCESS_EDIT,
	ACCESS_ADMIN,
	// functions
	getOwnerUuid,
	isOwner,
	hasAccess,
	accessLevelsThatInclude,
	allAccessLevels,
	inSharingList,
	getCitationStyle,
	getProjectAutoCitationsMode,
	getUserNameFromShare,
	timeTrackingTermsAccepted,
	timeTrackingTermsPendingReview,
	hasSharingSettingsAccess,
	hasTopicsEditingAccess,
	hasSourceNoteLabelsEditingAccess,
	canAddNewTopics,
	getDueDate,
	isProjectLocked,
	getProjectAiCreditUsage
};

// Expose for testing purposes only
const __testFns = (process.env.NODE_ENV === "test")
	? {_timeTrackingTermsRequired}
	: undefined;
export {__testFns};
